// A sensible drag / keyboard step for a numeric slider (ACT2-531).
//
// The naive `Math.max(1, span / 100)` breaks a sub-integer column: a cost column spanning 0.5 gets a step
// of 1, so the handle jumps straight to an extreme and can never land in between. This aims for ~100
// increments across the range, rounds to a "nice" 1 / 2 / 5 × 10ⁿ value so a committed threshold is a
// clean number, and only floors to 1 for an integer-valued column (so a whole-number field never lands
// on a fraction).

/** ~100 nice increments across `span`; floored to 1 when the column's values are whole numbers. */
export function defaultDragStep(span: number, integerValued: boolean): number {
  const raw = Math.max(Math.abs(span), Number.EPSILON) / 100;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude; // 1 .. 10
  const nice = (normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1) * magnitude;
  return integerValued ? Math.max(1, Math.round(nice)) : nice;
}

/**
 * Snap `v` to the nearest multiple of `step`, scrubbing IEEE-754 noise from the multiply.
 *
 * For an INTEGER step, `Math.round(v / step) * step` is already exact on safe integers, so it is returned
 * untouched — the old `toPrecision(12)` cleanup only *added* error there: `snapToStep(1712345678901, 1)`
 * rounded to `1712345678900`, and that corrupted value is what got COMMITTED into the predicate and pushed
 * verbatim into the synced rETL SQL (epoch-millis-magnitude columns are the realistic reach). A FRACTIONAL
 * step (the first of which `defaultDragStep` introduced) makes the bare multiply land on values like
 * `0.30000000000000004`; `toFixed(decimalsOf(step))` rounds to exactly the precision the step implies —
 * lossless, because a multiple of `step` never has more decimals than `step`.
 */
export function snapToStep(v: number, step: number): number {
  const snapped = Math.round(v / step) * step;
  return Number.isInteger(step) ? snapped : Number(snapped.toFixed(Math.min(100, decimalsOf(step))));
}

/** Decimal places a finite `step` carries (0 for an integer), handling `toString`'s exponential form. */
function decimalsOf(step: number): number {
  if (Number.isInteger(step)) return 0;
  const s = Math.abs(step).toString();
  const e = s.indexOf('e');
  if (e !== -1) {
    const mantissa = s.slice(0, e);
    const dot = mantissa.indexOf('.');
    const mantissaDecimals = dot === -1 ? 0 : mantissa.length - dot - 1;
    return Math.max(0, mantissaDecimals - Number(s.slice(e + 1)));
  }
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}
